/**
 * Source of truth: MTU `GEnx-1B_GenericInput_V5.xlsx`, revision V5 dated
 * 2026-08-10 (SHA-256: 74cfd59e5d2897d01f08293d4d558b29abb758e79b84f247657918a96a52bd39).
 * Component names and IINs come from `LLP Configs`; part numbers,
 * manufacture ranges, thrust variants, and cycle limits come from
 * `LLP_Table_FC`. The module groups are derived from the component names.
 *
 * MTU marks four part numbers as SB 72-0484 affected. Their limits below are
 * still the workbook's displayed baseline values, which apply when the serial
 * number is NOT listed in that SB. The current catalog shape cannot express a
 * serial-number-specific override, so each affected row is called out inline.
 * DR-3228 tracks adding SB-aware limit resolution.
 */
const VARIANTS = [
    'GEnx-1B64/P1',
    'GEnx-1B64/P2',
    'GEnx-1B67/P1',
    'GEnx-1B67/P2',
    'GEnx-1B70/P1',
    'GEnx-1B70/P2',
    'GEnx-1B70/75/P1',
    'GEnx-1B70/75/P2',
    'GEnx-1B74/75/P1',
    'GEnx-1B74/75/P2',
    'GEnx-1B76/P2',
];
const MANUFACTURE_RANGE = {
    start: '1999-01-01',
    end: '2099-01-01',
};
const NA = 'N/A';
function limitsByValue(partNumber, limits) {
    if (limits.length !== VARIANTS.length) {
        throw new Error(`GENX-1B ${partNumber} has ${limits.length} limits for ${VARIANTS.length} variants`);
    }
    return Object.fromEntries([...new Set(limits)].map((limit) => [String(limit), VARIANTS.filter((_, index) => limits[index] === limit)]));
}
function component(componentName, iin, parts) {
    return {
        componentName,
        iin,
        partNumbers: parts.map(([partNumber]) => partNumber),
        partLimits: Object.fromEntries(parts.map(([partNumber, limits]) => [partNumber, limitsByValue(partNumber, limits)])),
        partManufacture: Object.fromEntries(parts.map(([partNumber]) => [partNumber, MANUFACTURE_RANGE])),
    };
}
export const genx_1b = {
    engineType: 'GENX-1B',
    groups: [
        {
            groupName: 'Fan Module',
            components: [
                component('Fan Disk', '221', [
                    ['2300M90G01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                    ['2300M90G02', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('Booster Spool', '222', [
                    ['10039815', [20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, NA, NA]],
                    ['10079460', [20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000, 15000]],
                    ['10253777', [NA, 20000, NA, 20000, 20000, 15000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('Fwd Fan Shaft', '243', [
                    ['2117M43P01', [20000, 20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000]],
                    ['2117M43P02', [20000, 20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000]],
                    ['2321M44P01', [20000, 20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000]],
                    ['2321M44P02', [20000, 20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000]],
                ]),
                component('Fan Mid Shaft', '581', [
                    ['2331M20G02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2331M20G03', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M81G01', [4100, NA, 4100, NA, 4100, NA, 4100, NA, 4100, NA, NA]],
                    ['2332M81G03', [13800, 13100, 13800, 13100, 13800, 13100, 12100, 11100, 12100, 11100, 7800]],
                    ['2332M81G04', [13800, 13100, 13800, 13100, 13800, 13100, 12100, 11100, 12100, 11100, 7800]],
                    ['2332M81G05', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 12500]],
                    ['2332M91G01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 12500]],
                    ['2332M91G02', [13800, 13100, 13800, 13100, 13800, 13100, 12100, 11100, 12100, 11100, 7800]],
                    ['2440M91G04', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, NA]],
                    ['2440M91G05', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 12500]],
                    ['2440M91G06', [13800, 13100, 13800, 13100, 13800, 13100, 12100, 11100, 12100, 11100, 7800]],
                ]),
            ],
        },
        {
            groupName: 'HPC Module',
            components: [
                component('HPC Blisk 1', '317', [
                    ['2357M31G02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2357M31G03', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2445M81P01', [NA, 20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, 15000]],
                    ['2448M41P01', [20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, NA, NA]],
                ]),
                component('HPC Blisk 2', '318', [
                    ['2303M52P02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2311M52G02', [20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, NA, NA]],
                    ['2445M82P01', [NA, 20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, 15000]],
                    ['2480M12G01', [NA, 20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, 15000]],
                ]),
                component('HPC Spool 3-4', '314', [
                    ['2357M34G01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2439M37G01', [12300, 12700, 12300, 12700, 12300, 12700, 12300, 10900, 12300, 10900, 9800]],
                    ['2439M37G02', [12300, 12700, 12300, 12700, 12300, 12700, 12300, 10900, 12300, 10900, 9800]],
                ]),
                component('HPC Blisk 5', '319', [
                    ['2357M35P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                    ['2357M35P02', [18900, 20000, 18900, 20000, 18900, 20000, 15000, 15000, 15000, 15000, 15000]],
                    ['2820M57P01', [16500, 17000, 16500, 17000, 16500, 17000, 15000, 15000, 15000, 15000, 14300]],
                    ['2921M03P01', [NA, 20000, NA, 20000, NA, 20000, NA, 20000, NA, 15000, 15000]],
                    ['2921M03P02', [NA, 20000, NA, 20000, NA, 20000, NA, 20000, NA, 15000, 15000]],
                    ['2929M11P01', [NA, 17000, NA, 17000, NA, 17000, NA, 15000, NA, 15000, 14300]],
                ]),
                component('HPC Spool 6-10', '313', [
                    ['2357M30G01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2357M30G02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2439M35G01', [16600, NA, 16600, NA, 16600, NA, 15000, NA, 15000, NA, NA]],
                    ['2439M35G02', [NA, 17000, NA, 17000, NA, 17000, NA, 15000, NA, 15000, 14600]],
                    ['2445M40G02', [NA, 17000, NA, 17000, NA, 17000, NA, 15000, NA, 15000, 14600]],
                    ['2610M90G01', [NA, 17000, NA, 17000, NA, 17000, NA, 15000, NA, 15000, 14600]],
                    // SB 72-0484 can override this baseline for affected serial numbers (DR-3228).
                    ['2628M56G01', [NA, 17000, NA, 17000, NA, 17000, NA, 15000, NA, 15000, 14600]],
                    ['2628M56G02', [NA, 17000, NA, 17000, NA, 17000, NA, 17000, NA, 15000, 14600]],
                ]),
                component('HPC Rotor Fwd Shaft', '315', [
                    ['2357M38P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('HPC Tube Supporter Ring', '312', [
                    ['2357M39P01', [20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('HPC CDP Seal', '316', [
                    ['2116M68P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                    // SB 72-0484 can override this baseline for affected serial numbers (DR-3228).
                    ['2383M82P03', [NA, 20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, 15000]],
                    ['2383M82P04', [NA, 20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, 15000]],
                    ['2445M74P01', [NA, 20000, NA, 20000, NA, 20000, NA, 15000, NA, 15000, 14800]],
                ]),
            ],
        },
        {
            groupName: 'HPT Module',
            components: [
                component('Combustor Case', '412', [
                    ['2303M23G01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2303M23G06', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2303M23G11', [10500, 11800, 10500, 11800, 10500, 11800, 10500, 10000, 10500, 10000, 8200]],
                    ['2303M23G19', [10500, NA, 10500, NA, 10500, NA, 10500, NA, 10500, NA, NA]],
                    ['2303M23G20', [NA, 11800, NA, 11800, NA, 11800, NA, 10000, NA, 10000, 8200]],
                    ['2303M23G21', [NA, 11800, NA, 11800, NA, 11800, NA, 10000, NA, 10000, 8200]],
                    ['2303M23G23', [10500, 11800, 10500, 11800, 10500, 11800, 10500, 10000, 10500, 10000, 8200]],
                    ['2303M23G25', [10500, 11800, 10500, 11800, 10500, 11800, 10500, 10000, 10500, 10000, 8200]],
                    ['2405M02G01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2417M02G01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2417M03G01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                ]),
                component('HPT Case', '52A', [
                    ['2302M90G01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2302M90G04', [10900, 14200, 10900, 14200, 10900, 14200, 10900, 11500, 10900, 11500, 8700]],
                    ['2302M90G09', [10900, 14200, 10900, 14200, 10900, 14200, 10900, 11500, 10900, 11500, 8700]],
                    ['2417M00G02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                ]),
                component('HPT Disk 1', '531', [
                    ['2352M61G02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2352M61G03', [14600, NA, 14600, NA, 14600, NA, 12200, NA, 12200, NA, NA]],
                    ['2383M83G03', [NA, 13900, NA, 13900, NA, 13900, NA, 12300, NA, 12300, 10700]],
                ]),
                component('HPT Disk 2', '532', [
                    ['2300M84P02', [11400, 15000, 11400, 15000, 11400, 15000, 11300, 14000, 11300, 14000, NA]],
                    // SB 72-0484 can override this baseline for affected serial numbers (DR-3228).
                    ['2383M86P02', [NA, 12100, NA, 12100, NA, 12100, NA, 10400, NA, 10400, 9500]],
                    ['2383M86P03', [NA, 12100, NA, 12100, NA, 12100, NA, 10400, NA, 10400, 9500]],
                    ['2445M92P01', [NA, 13600, NA, 13600, NA, 13600, NA, 12700, NA, 12700, 11600]],
                ]),
                component('HPT Fwd Seal', '53B', [
                    ['2352M60P01', [8600, NA, 8600, NA, 8600, NA, 8600, NA, 8600, NA, NA]],
                    ['2417M60P01', [14700, NA, 14700, NA, 14700, NA, 10900, NA, 10900, NA, NA]],
                    ['2417M60P02', [NA, 15000, NA, 15000, NA, 15000, NA, 14000, NA, 14000, 10100]],
                    ['2417M60P03', [NA, 15000, NA, 15000, NA, 15000, NA, 14000, NA, 14000, 10100]],
                    ['2756M01P01', [14700, NA, 14700, NA, 14700, NA, 10900, NA, 10900, NA, NA]],
                    ['2759M04P01', [NA, 15000, NA, 15000, NA, 15000, NA, 14000, NA, 14000, 10100]],
                    ['2793M25P01', [14700, NA, 14700, NA, 14700, NA, 10900, NA, 10900, NA, NA]],
                ]),
                component('HPT Aft Seal', '53K', [
                    ['2116M63P01', [7000, NA, 7000, NA, 7000, NA, 7000, NA, 7000, NA, NA]],
                    ['2116M63P02', [15000, 15000, 15000, 15000, 15000, 15000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('HPT Interstage Seal', '53M', [
                    ['2352M63P01', [15000, NA, 15000, NA, 15000, NA, 15000, NA, 15000, NA, NA]],
                    ['2352M63P02', [15000, NA, 15000, NA, 15000, NA, 15000, NA, 15000, NA, NA]],
                    ['2383M85P03', [NA, 15000, NA, 15000, NA, 15000, NA, 15000, NA, 15000, 14200]],
                    ['2383M85P05', [NA, 15000, NA, 15000, NA, 15000, NA, 15000, NA, 15000, 14200]],
                    // SB 72-0484 can override this baseline for affected serial numbers (DR-3228).
                    ['2383M85P04', [NA, 15000, NA, 15000, NA, 15000, NA, 15000, NA, 15000, 14200]],
                    ['2445M71P01', [NA, 15000, NA, 15000, NA, 15000, NA, 15000, NA, 15000, 14600]],
                    ['2445M71P02', [NA, 15000, NA, 15000, NA, 15000, NA, 15000, NA, 15000, 14600]],
                ]),
            ],
        },
        {
            groupName: 'LPT Module',
            components: [
                component('LPT Disk 1', '56A', [
                    ['2331M40P01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M51P01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M61P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('LPT Disk 2', '56B', [
                    ['2331M41P01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M52P01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M62P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('LPT Disk 3', '56C', [
                    ['2331M42P03', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M53P02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M63P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('LPT Disk 4', '56D', [
                    ['2331M43P03', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M54P02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M64P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('LPT Disk 5', '56E', [
                    ['2331M44P04', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M55P02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M65P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('LPT Cone Shaft', '565', [
                    ['2331M53G01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('LPT Disk 6', '56F', [
                    ['2331M12P02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M56P01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M66P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
                component('LPT Disk 7', '56P', [
                    ['2331M13P02', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M57P01', [NA, NA, NA, NA, NA, NA, NA, NA, NA, NA, NA]],
                    ['2332M67P01', [20000, 20000, 20000, 20000, 20000, 20000, 15000, 15000, 15000, 15000, 15000]],
                ]),
            ],
        },
    ],
};
//# sourceMappingURL=genx-1b.js.map